// Shared filter/table controls for the lab dashboards (LLM Lab, TTS Lab).
// Extracted verbatim from LLMLabPage; SortHeader/SortState are generic over
// the page's sort-key union so each lab keeps its own column set.

import { useEffect, useRef, useState } from "react";

import { Text, View } from "../base";
import { Icon } from "../base/Icon";

export type SortState<K extends string> = { key: K; dir: "asc" | "desc" };

export const PillGroup = <T extends string>({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: T; icon?: string }[];
  value: T;
  onChange: (next: T) => void;
}) => (
  <View className="flex flex-row gap-1 flex-wrap">
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value || "default"}
          onClick={() => onChange(opt.value)}
          className={`flex flex-row items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors cursor-pointer border ${
            active
              ? "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
              : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 border-transparent"
          }`}
        >
          {opt.icon && (
            <Icon
              name={opt.icon}
              size={12}
              className={`shrink-0 ${active ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"}`}
            />
          )}
          {opt.label}
        </button>
      );
    })}
  </View>
);

export const FilterSection = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <View className="flex flex-col gap-1.5">
    <Text className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">{label}</Text>
    {children}
  </View>
);

/**
 * MultiSelectField — looks like DropdownField but its popover is a list of
 * checkboxes. Used for filters where users want to compose multiple values
 * (e.g., token-bucket ranges).
 *
 * Trigger summarizes the selection: emptyLabel when empty, the single value
 * when one is picked, "N selected" otherwise.
 */
export const MultiSelectField = <T extends string>({
  options,
  value,
  onChange,
  emptyLabel = "All",
  className,
}: {
  options: readonly T[];
  value: T[];
  onChange: (next: T[]) => void;
  emptyLabel?: string;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const summary = value.length === 0 ? emptyLabel : value.length === 1 ? value[0] : `${value.length} selected`;

  const toggle = (opt: T) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };

  return (
    <View
      ref={containerRef as any}
      className={`relative ${className ?? ""}`}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex flex-row items-center justify-between w-full h-[38px] px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white cursor-pointer hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
      >
        <span className="truncate">{summary}</span>
        <Icon
          name={open ? "chevron-up" : "chevron-down"}
          size={12}
          className="text-gray-400 dark:text-gray-500 shrink-0 ml-2"
        />
      </button>
      {open && (
        <View className="absolute left-0 right-0 top-full mt-1 z-30 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl py-1">
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="block w-full text-left px-3 py-1.5 text-[12px] text-blue-500 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800/60 cursor-pointer"
            >
              Clear all
            </button>
          )}
          {options.map((opt) => {
            const selected = value.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className="flex flex-row items-center gap-2 w-full px-3 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800/60 cursor-pointer"
              >
                <View
                  className={`flex items-center justify-center w-4 h-4 rounded border ${
                    selected ? "bg-blue-500 border-blue-500" : "bg-transparent border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {selected && (
                    <Icon
                      name="check"
                      size={12}
                      className="text-white"
                    />
                  )}
                </View>
                <span>{opt}</span>
              </button>
            );
          })}
        </View>
      )}
    </View>
  );
};

export const SortHeader = <K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  align = "right",
}: {
  label: string;
  sortKey: K;
  sort: SortState<K>;
  onSort: (key: K) => void;
  align?: "left" | "right";
}) => {
  const active = sort.key === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`flex flex-row items-center gap-1 cursor-pointer transition-colors ${
        align === "right" ? "justify-end" : "justify-start"
      } ${active ? "text-gray-700 dark:text-gray-200" : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"}`}
    >
      <span>{label}</span>
      <Icon
        name={active ? (sort.dir === "asc" ? "chevron-up" : "chevron-down") : "chevron-down"}
        size={10}
        className={active ? "" : "opacity-0"}
      />
    </button>
  );
};
